import { Alert, CheckboxField } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { useTranslation } from "react-i18next";
import {
	OPTIONAL_SCOPES,
	type OptionalScope,
} from "src/features/integrations/sources/fortnox/constants.js";

interface FortnoxScopeSelectionProps {
	selectedScopes: OptionalScope[];
	onChange: (scopes: OptionalScope[]) => void;
	/** Re-authorization only: the data source's current scopes are loading. */
	isLoading?: boolean;
	/** Re-authorization only: the data source's current scopes failed to load. */
	isError?: boolean;
	onRetry?: () => void;
}

export const FortnoxScopeSelection = ({
	selectedScopes,
	onChange,
	isLoading = false,
	isError = false,
	onRetry,
}: FortnoxScopeSelectionProps) => {
	const { t } = useTranslation();
	const scopeText: Record<
		OptionalScope,
		{ label: string; supportingText: string }
	> = {
		salary: {
			label: t("Salary data"),
			supportingText: t("Access to salary information"),
		},
		assets: {
			label: t("Assets data"),
			supportingText: t("Access to assets information"),
		},
		timereporting: {
			label: t("Time reporting"),
			supportingText: t("Access to time reporting information"),
		},
	};

	const toggle = (scope: OptionalScope, checked: boolean) =>
		onChange(
			OPTIONAL_SCOPES.filter((option) =>
				option === scope ? checked : selectedScopes.includes(option),
			),
		);

	if (isError) {
		return (
			<Alert
				variant="error"
				className="mb-4"
				title={t("Could not load the data source's current scopes")}
				actions={
					<Button variant="secondaryGray" size="sm" onClick={onRetry}>
						{t("Try again")}
					</Button>
				}
			/>
		);
	}

	return (
		<fieldset className="mb-4" disabled={isLoading}>
			<legend className="mb-3 text-sm font-medium text-gray-700">
				{t("Select additional data scopes (optional)")}
			</legend>
			<div className="flex flex-col gap-2">
				{OPTIONAL_SCOPES.map((scope) => (
					<CheckboxField
						key={scope}
						label={scopeText[scope].label}
						supportingText={scopeText[scope].supportingText}
						checked={selectedScopes.includes(scope)}
						onChange={(event) => toggle(scope, event.target.checked)}
					/>
				))}
			</div>
		</fieldset>
	);
};
